import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import {
  ASCENDING_DESCENDING_COPY,
  SORTING_ORDER,
  SortingDirectionValues,
} from 'models/Sort';

// Maps sort option → backend sort field name.
export const SUGGESTED_ACTIONS_SORT_VALUES = {
  dealName: 'sortName',
  suggestionNumber: 'suggestedActionsCount',
} as const;

// Sort value → i18n key for the option label.
const MULTI_SORT_COPY: Record<string, string> = {
  [SUGGESTED_ACTIONS_SORT_VALUES.dealName]: 'dealName',
  [SUGGESTED_ACTIONS_SORT_VALUES.suggestionNumber]: 'suggestionNumber',
};

// Sort value → direction copy. Fields without an entry fall back to A-Z / Z-A.
const MULTI_SORT_DIRECTION_COPY: Record<
  string,
  Record<string, string> | undefined
> = {
  [SUGGESTED_ACTIONS_SORT_VALUES.suggestionNumber]: ASCENDING_DESCENDING_COPY,
};

const useSuggestedActionsMultiSort = () => {
  const { t } = useTranslation();

  const sortValues = useMemo(
    () => Object.values(SUGGESTED_ACTIONS_SORT_VALUES),
    [],
  );

  const multiSortValues = useMemo(
    () =>
      sortValues.map((value) => ({
        value,
        label: t(MULTI_SORT_COPY[value]),
      })),
    [sortValues, t],
  );

  const multiSortDirectionTranslation = useMemo(() => {
    const result: SortingDirectionValues = {};

    for (const value of sortValues) {
      const copy = MULTI_SORT_DIRECTION_COPY[value];

      result[value] = {
        [SORTING_ORDER.A_TO_Z]: t(copy?.[SORTING_ORDER.A_TO_Z] || 'aToZ'),
        [SORTING_ORDER.Z_TO_A]: t(copy?.[SORTING_ORDER.Z_TO_A] || 'zToA'),
      };
    }

    return result;
  }, [sortValues, t]);

  return { multiSortValues, multiSortDirectionTranslation };
};

export default useSuggestedActionsMultiSort;
